import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SIDEBAR_GROUPS } from "@/lib/sidebarShortcuts";
import { stampNow } from "@/lib/dates";

// Colour chip per row so every shortcut is recognisable at a glance.
const PALETTE = [
  { bg: "bg-rose-100", text: "text-rose-700", bar: "bg-rose-500", hover: "hover:bg-rose-50" },
  { bg: "bg-amber-100", text: "text-amber-700", bar: "bg-amber-500", hover: "hover:bg-amber-50" },
  { bg: "bg-emerald-100", text: "text-emerald-700", bar: "bg-emerald-500", hover: "hover:bg-emerald-50" },
  { bg: "bg-sky-100", text: "text-sky-700", bar: "bg-sky-500", hover: "hover:bg-sky-50" },
  { bg: "bg-violet-100", text: "text-violet-700", bar: "bg-violet-500", hover: "hover:bg-violet-50" },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-700", bar: "bg-fuchsia-500", hover: "hover:bg-fuchsia-50" },
  { bg: "bg-teal-100", text: "text-teal-700", bar: "bg-teal-500", hover: "hover:bg-teal-50" },
  { bg: "bg-orange-100", text: "text-orange-700", bar: "bg-orange-500", hover: "hover:bg-orange-50" },
];

const GROUP_TINT = {
  Add: "text-rose-600",
  Display: "text-sky-600",
};

// Fixed Busy-style shortcut panel on the right. Desktop only: hidden below lg.
export const ShortcutSidebar = ({ allowed = [] }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [now, setNow] = useState(stampNow());
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "1");

  useEffect(() => {
    const t = setInterval(() => setNow(stampNow()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", collapsed ? "1" : "0");
    document.documentElement.style.setProperty("--sb-w", collapsed ? "26px" : "210px");
  }, [collapsed]);

  const groups = SIDEBAR_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.perm || allowed.includes(i.perm)),
  })).filter((g) => g.items.some((i) => i.to || i.action));

  if (collapsed)
    return (
      <aside
        data-testid="shortcut-sidebar-collapsed"
        className="fixed right-0 top-0 z-[60] hidden h-screen w-[26px] flex-col items-center border-l border-[#e4e4e7] bg-gradient-to-b from-rose-500 via-fuchsia-500 to-sky-500 lg:flex"
      >
        <button
          type="button"
          data-testid="sidebar-expand-btn"
          title="Show shortcuts"
          onClick={() => setCollapsed(false)}
          className="flex h-8 w-full items-center justify-center text-white hover:bg-black/20"
        >
          <ChevronLeft size={15} />
        </button>
        <span className="mt-3 select-none text-[10px] font-bold uppercase tracking-[0.25em] text-white [writing-mode:vertical-rl]">
          Shortcuts
        </span>
      </aside>
    );

  return (
    <aside
      data-testid="shortcut-sidebar"
      className="fixed right-0 top-0 z-[60] hidden h-screen w-[210px] flex-col border-l border-[#e4e4e7] bg-white lg:flex"
    >
      <div className="flex items-center justify-between border-b border-[#e4e4e7] bg-gradient-to-r from-rose-500 via-fuchsia-500 to-sky-500 px-3 py-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-white">Shortcuts</p>
        <button
          type="button"
          data-testid="sidebar-collapse-btn"
          title="Hide shortcuts"
          onClick={() => setCollapsed(true)}
          className="rounded p-[2px] text-white hover:bg-black/20"
        >
          <ChevronRight size={15} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {groups.map((g) => (
          <div key={g.title} className="mb-3">
            <p
              className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] ${
                GROUP_TINT[g.title] || "text-[#71717a]"
              }`}
            >
              {g.title}
            </p>
            {g.items.map((i, idx) => {
              const active = i.to && pathname === i.to.split("?")[0];
              const free = !i.to && !i.action;
              const c = PALETTE[idx % PALETTE.length];
              return (
                <button
                  key={i.key}
                  type="button"
                  disabled={free}
                  data-testid={`sidebar-${i.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => {
                    if (i.action === "sales-rate") window.dispatchEvent(new Event("open-sales-rate"));
                    else if (i.to) navigate(i.to);
                  }}
                  className={`group flex w-full items-center gap-2 py-[5px] pr-3 text-left text-[12.5px] transition-colors ${
                    active ? "bg-[#09090b] text-white" : free ? "cursor-default" : `text-[#27272a] ${c.hover}`
                  }`}
                >
                  <span
                    className={`h-[18px] w-[3px] shrink-0 rounded-r ${
                      active ? "bg-white" : free ? "bg-transparent" : c.bar
                    }`}
                  />
                  <span
                    className={`w-[54px] shrink-0 rounded px-1 py-[1px] text-center font-mono text-[10.5px] font-bold ${
                      active
                        ? "bg-white/20 text-white"
                        : free
                          ? "text-[#d4d4d8]"
                          : `${c.bg} ${c.text}`
                    }`}
                  >
                    {i.key}
                  </span>
                  <span className="truncate">{i.label || "—"}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
        <p className="border-t border-[#e4e4e7] bg-[#fafafa] px-3 py-1.5 text-center font-mono text-[11px] font-semibold text-[#3f3f46]" data-testid="sidebar-clock">
          {now}
        </p>
      <div className="border-t border-[#e4e4e7] bg-[#fafafa] px-3 py-2 text-[10px] leading-relaxed text-[#71717a]">
        Hold Ctrl and press the key shown, or click a row. F1 shows all shortcuts · F2 saves the open form.
      </div>
    </aside>
  );
};
